import { FlexRow } from '@/components/common/Flex';
import { Box, forwardRef } from '@chakra-ui/react';
import { CountryCode } from '@common/atomic/types';
import { Country } from '@common/types';
import { convertUnionToArray } from '@common/util/typebox';
import { chakraComponents } from 'chakra-react-select';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import isEqual from 'lodash.isequal';
import React, { useMemo } from 'react';
import SelectInput, { SelectInputProps } from './SelectInput';

// TODO: Keep this in sync with mobile
// Moving it all to common/ seems to cause Webpack issues
countries.registerLocale(enLocale);
type CountryOption = {
    alpha3: Country;
    alpha2: string;
    alias: string;
};
export const alpha3ToAlpha2 = (alpha3: string) => countries.toAlpha2(alpha3);
export const alphaToAlias = (alpha: string) =>
    countries.getName(alpha, 'en', { select: 'alias' });
const getCountries = (excludeCountries: Country[]): CountryOption[] =>
    convertUnionToArray(CountryCode)
        .map((alpha3) => {
            const alpha2 = alpha3ToAlpha2(alpha3);
            const alias = alphaToAlias(alpha3);
            return { alpha3, alpha2, alias };
        })
        .filter(({ alpha3 }) => !excludeCountries.includes(alpha3));

interface CountryInputProps<V = any>
    extends Omit<SelectInputProps<V>, 'value' | 'options'> {
    value: Country;
    excludeCountries?: Country[];
}

function CountryInputInner<V = any>(
    { value, onChange, excludeCountries = [], ...props }: CountryInputProps<V>,
    ref: React.ForwardedRef<HTMLSelectElement>
) {
    const countryOptions = useMemo(
        () => getCountries(excludeCountries),
        [excludeCountries]
    );

    return (
        <SelectInput
            ref={ref}
            components={{
                Option,
                SingleValue,
            }}
            options={countryOptions.map(({ alpha3, alpha2, alias }) => ({
                value: [alpha3, alpha2].join('-'),
                label: alias,
            }))}
            value={[
                value,
                countryOptions.find(({ alpha3 }) => isEqual(alpha3, value))
                    ?.alpha2 || '',
            ].join('-')}
            onChange={(value) => onChange?.(value.split('-')[0])}
            {...props}
        />
    );
}

const CountryInput = forwardRef<CountryInputProps, 'select'>(CountryInputInner);

export default CountryInput;

const Option = ({ children, value, ...props }: any) => {
    const alpha3 = value.split('-')[0];
    const alpha2 = value.split('-')[1].toLowerCase();
    return (
        <chakraComponents.Option value={alpha3} {...props}>
            <Box className={`fi fi-${alpha2}`} mr={3} />
            {children}
        </chakraComponents.Option>
    );
};

const SingleValue = ({ children, ...props }: any) => {
    const value = props.getValue()[0]?.value;
    const alpha2 = value.split('-')[1].toLowerCase();
    return (
        <chakraComponents.SingleValue {...props}>
            <FlexRow alignItems="center">
                <Box className={`fi fi-${alpha2}`} mr={2} />
                {children}
            </FlexRow>
        </chakraComponents.SingleValue>
    );
};
